import { AlertCircle, Shield, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function NoAccess({ user }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-md3-surface p-4">
      <Card className="max-w-2xl w-full shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
          <CardTitle className="text-2xl text-md3-title-medium text-md3-on-surface">No Page Access</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Info */}
          <div className="bg-md3-surface rounded-md3-lg p-4">
            <p className="text-md3-body-small text-md3-on-surface-variant mb-2">You are logged in as:</p>
            <div className="space-y-1">
              <p className="font-semibold text-md3-on-surface">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-md3-body-small text-md3-on-surface-variant">
                {user?.department} - {user?.access_level}
              </p>
              <p className="text-xs text-md3-on-surface-variant">
                Role: {user?.role_id}
              </p>
            </div>
          </div>

          {/* Message */}
          <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-md3-lg">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-md3-body-small font-medium text-yellow-800">
                You don't have access to any pages in the system.
              </p>
              <p className="text-md3-body-small text-yellow-700 mt-1">
                Your role "{user?.role_id || 'Unknown'}" currently has no page permissions configured.
              </p>
            </div>
          </div>

          {/* What to do */}
          <div className="space-y-3">
            <p className="text-md3-body-small font-medium text-md3-on-surface">What can you do?</p>
            <ul className="space-y-2 text-md3-body-small text-md3-on-surface-variant">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 text-gray-400" />
                <span>
                  Contact your system administrator or IT department
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 mt-0.5 text-gray-400" />
                <span>
                  Request access to the pages you need for your work
                </span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 text-gray-400" />
                <span>
                  Verify that your role and department are correctly assigned
                </span>
              </li>
            </ul>
          </div>

          {/* Admin info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md3-lg">
            <p className="text-md3-body-small font-medium text-blue-900 mb-1">
              For Administrators:
            </p>
            <p className="text-md3-body-small text-blue-700">
              Go to Settings → Role & Permissions → Select "{user?.role_id}" → 
              Edit Permissions → Grant view access to at least one page
            </p>
          </div>

          {/* Logout button */}
          <div className="pt-4 border-t">
            <Button
              onClick={() => {
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                window.location.href = "/login";
              }}
              variant="outline"
              className="w-full"
            >
              Logout
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
